import React, { FC } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { getVideoPoster } from '../utils/imageTransform';

interface VideoPosterProps {
  url: string;
  duration?: number;
  sx?: SxProps<Theme>;
  /** Play badge marks the still as video; hide it where space is very tight. */
  badge?: boolean;
  /**
   * Requested still width. Set this to roughly the rendered size at ~3x DPR —
   * the default suits a full-width preview and is far too large for a list
   * thumbnail.
   */
  width?: number;
}

/**
 * A non-playing still for a video, for surfaces that only ever show a thumbnail
 * (feed cards, crosspost embeds, upload previews).
 *
 * These used to render a <video preload="metadata">, which made the browser
 * fetch container metadata for every video on screen just to paint one frame.
 * ImageKit derives the still from the URL, so this is a plain image instead.
 */
const VideoPoster: FC<VideoPosterProps> = ({
  url,
  duration,
  sx,
  badge = true,
  width = 800,
}) => {
  const poster = getVideoPoster(url, duration, width);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {poster ? (
        <Box component="img" src={poster} alt="" sx={sx} />
      ) : (
        // No derivable still (non-ImageKit media) — fall back to the first
        // frame rather than showing an empty box.
        <Box component="video" src={url} muted playsInline preload="metadata" sx={sx} />
      )}
      {badge && (
        <Box
          sx={{
            position: 'absolute',
            insetBlockEnd: 4,
            insetInlineEnd: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: 'var(--radius-circle)',
            backgroundColor: 'rgba(0,0,0,0.6)',
            pointerEvents: 'none',
          }}
        >
          <PlayArrowIcon sx={{ fontSize: 14, color: '#fff' }} />
        </Box>
      )}
    </Box>
  );
};

export default VideoPoster;
